/**
 * @yada/i18n — central i18next configuration
 *
 * Usage (shell entry-point):
 *   import '@yada/i18n';   // side-effect: initialises i18next
 *
 * Usage (components):
 *   import { useTranslation } from '@yada/i18n';
 *   const { t } = useTranslation('common');
 *
 * The instance is a singleton. Importing this module from multiple packages
 * in the same bundle is safe — i18next guards against double-init.
 */
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import type { SupportedLocale } from './types';
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY } from './types';

// ─── English ─────────────────────────────────────────────────────────────────
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enSettings from './locales/en/settings.json';
import enHabits from './locales/en/habits.json';
import enChat from './locales/en/chat.json';
import enDocs from './locales/en/docs.json';
import enCalendar from './locales/en/calendar.json';
import enMealPlanner from './locales/en/meal-planner.json';
import enGroups from './locales/en/groups.json';
import enTodo from './locales/en/todo.json';
import enShoppingList from './locales/en/shopping-list.json';
import enContacts from './locales/en/contacts.json';
import enAdmin from './locales/en/admin.json';
import enDevtools from './locales/en/devtools.json';
import enEmoji from './locales/en/emoji.json';
import enCommunityVotes from './locales/en/community-votes.json';
import enContentDistribution from './locales/en/content-distribution.json';
import enEvents from './locales/en/events.json';
import enTrips from './locales/en/trips.json';
import enTraining from './locales/en/training.json';
import enFinance from './locales/en/finance.json';

// ─── Polish ───────────────────────────────────────────────────────────────────
import plCommon from './locales/pl/common.json';
import plAuth from './locales/pl/auth.json';
import plSettings from './locales/pl/settings.json';
import plHabits from './locales/pl/habits.json';
import plChat from './locales/pl/chat.json';
import plDocs from './locales/pl/docs.json';
import plCalendar from './locales/pl/calendar.json';
import plMealPlanner from './locales/pl/meal-planner.json';
import plGroups from './locales/pl/groups.json';
import plTodo from './locales/pl/todo.json';
import plShoppingList from './locales/pl/shopping-list.json';
import plContacts from './locales/pl/contacts.json';
import plAdmin from './locales/pl/admin.json';
import plDevtools from './locales/pl/devtools.json';
import plEmoji from './locales/pl/emoji.json';
import plCommunityVotes from './locales/pl/community-votes.json';
import plContentDistribution from './locales/pl/content-distribution.json';
import plEvents from './locales/pl/events.json';
import plTrips from './locales/pl/trips.json';
import plTraining from './locales/pl/training.json';
import plFinance from './locales/pl/finance.json';

// ─── Spanish ─────────────────────────────────────────────────────────────────
import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esSettings from './locales/es/settings.json';
import esHabits from './locales/es/habits.json';
import esChat from './locales/es/chat.json';
import esDocs from './locales/es/docs.json';
import esCalendar from './locales/es/calendar.json';
import esMealPlanner from './locales/es/meal-planner.json';
import esGroups from './locales/es/groups.json';
import esTodo from './locales/es/todo.json';
import esShoppingList from './locales/es/shopping-list.json';
import esContacts from './locales/es/contacts.json';
import esAdmin from './locales/es/admin.json';
import esDevtools from './locales/es/devtools.json';
import esEmoji from './locales/es/emoji.json';
import esCommunityVotes from './locales/es/community-votes.json';
import esContentDistribution from './locales/es/content-distribution.json';
import esEvents from './locales/es/events.json';
import esTrips from './locales/es/trips.json';
import esTraining from './locales/es/training.json';
import esFinance from './locales/es/finance.json';

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    settings: enSettings,
    habits: enHabits,
    chat: enChat,
    docs: enDocs,
    calendar: enCalendar,
    'meal-planner': enMealPlanner,
    groups: enGroups,
    todo: enTodo,
    'shopping-list': enShoppingList,
    contacts: enContacts,
    admin: enAdmin,
    devtools: enDevtools,
    emoji: enEmoji,
    'community-votes': enCommunityVotes,
    'content-distribution': enContentDistribution,
    events: enEvents,
    trips: enTrips,
    training: enTraining,
    finance: enFinance,
  },
  pl: {
    common: plCommon,
    auth: plAuth,
    settings: plSettings,
    habits: plHabits,
    chat: plChat,
    docs: plDocs,
    calendar: plCalendar,
    'meal-planner': plMealPlanner,
    groups: plGroups,
    todo: plTodo,
    'shopping-list': plShoppingList,
    contacts: plContacts,
    admin: plAdmin,
    devtools: plDevtools,
    emoji: plEmoji,
    'community-votes': plCommunityVotes,
    'content-distribution': plContentDistribution,
    events: plEvents,
    trips: plTrips,
    training: plTraining,
    finance: plFinance,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    settings: esSettings,
    habits: esHabits,
    chat: esChat,
    docs: esDocs,
    calendar: esCalendar,
    'meal-planner': esMealPlanner,
    groups: esGroups,
    todo: esTodo,
    'shopping-list': esShoppingList,
    contacts: esContacts,
    admin: esAdmin,
    devtools: esDevtools,
    emoji: esEmoji,
    'community-votes': esCommunityVotes,
    'content-distribution': esContentDistribution,
    events: esEvents,
    trips: esTrips,
    training: esTraining,
    finance: esFinance,
  },
} as const;

if (!i18next.isInitialized) {
  i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,

      // Fallback chain: user locale → English → raw key
      fallbackLng: DEFAULT_LOCALE,

      // Default namespace for `useTranslation()` with no argument
      defaultNS: 'common',

      // Language detection order and persistence
      detection: {
        // 1. Check localStorage key we set ourselves
        order: ['localStorage', 'navigator', 'htmlTag'],
        lookupLocalStorage: LOCALE_STORAGE_KEY,
        caches: ['localStorage'],
        htmlTag: typeof document !== 'undefined' ? document.documentElement : undefined,
      },

      interpolation: {
        // React already escapes content – no need for i18next to double-escape
        escapeValue: false,
      },

      // Surface missing keys in development; fail silently in production
      saveMissing: process.env.NODE_ENV === 'development',
      missingKeyHandler: (lngs, ns, key) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[i18n] Missing key: ${ns}:${key} for locale(s): ${lngs.join(', ')}`);
        }
      },
    });
}

export default i18next;

/** Custom DOM event name emitted when the language preference changes. */
export const LOCALE_CHANGE_EVENT = 'yada:locale-change';

/**
 * Programmatically change the active locale and persist the choice.
 * Prefer calling this from the LanguageSelector component rather than
 * touching i18next directly.
 */
export async function changeLocale(locale: SupportedLocale): Promise<void> {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  await i18next.changeLanguage(locale);
  // Update the <html lang=""> attribute for accessibility & SEO
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
  // Notify the runtime so it can broadcast updated locale context to all apps
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LOCALE_CHANGE_EVENT, { detail: { locale } }));
  }
}

/**
 * Returns the currently active locale, guaranteed to be one of the
 * supported locales (falls back to DEFAULT_LOCALE if detection returns
 * something we don't support).
 */
export function getCurrentLocale(): SupportedLocale {
  const lang = i18next.language as SupportedLocale;
  const supported: SupportedLocale[] = ['en', 'pl', 'es'];
  // Strip region codes (e.g. "en-US" → "en")
  const base = lang?.split('-')[0] as SupportedLocale;
  return supported.includes(base) ? base : DEFAULT_LOCALE;
}
